/**
 * TrashPage - Gestione Cestino
 * Il Diario di Bordo - SaaS
 * 
 * Funzionalità:
 * - Lista entries nel cestino con filtri
 * - Statistiche e countdown scadenza
 * - Ripristino singolo/multiplo con check duplicati
 * - Merge entries duplicate
 * - Eliminazione definitiva (coordinatori)
 * - Log attività recenti
 * 
 * FIX 29/12/2024:
 * - Corretto passaggio confirmText a trashApi.empty()
 * - EmptyTrashModal ora passa confirmText a onConfirm
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import { trashApi } from '../../services/api'
import { Card, Badge, EmptyState, ConfirmModal } from '../../components/ui'
import { 
  Trash2, 
  RotateCcw, 
  AlertTriangle, 
  Clock, 
  CheckCircle, 
  X,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Eye,
  Trash,
  Users,
  Calendar,
  FileText,
  Activity,
  AlertCircle,
  Check,
  XCircle
} from 'lucide-react'

// Helper per formattare data
const formatDate = (date) => {
  if (!date) return '-'
  return new Date(date).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

const formatDateTime = (date) => {
  if (!date) return '-'
  return new Date(date).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatRelativeTime = (date) => {
  if (!date) return ''
  const now = new Date()
  const then = new Date(date)
  const diffMs = now - then
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  
  if (diffMins < 60) return `${diffMins} minuti fa`
  if (diffHours < 24) return `${diffHours} ore fa`
  if (diffDays === 1) return 'ieri'
  if (diffDays < 7) return `${diffDays} giorni fa`
  return formatDate(date)
}

// Badge scadenza colorato
const ExpirationBadge = ({ giorni, urgenza }) => {
  const colors = {
    low: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
  }
  
  const icons = {
    low: <Clock className="h-3 w-3" />,
    medium: <Clock className="h-3 w-3" />,
    high: <AlertTriangle className="h-3 w-3" />
  }
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${colors[urgenza] || colors.low}`}>
      {icons[urgenza]}
      {giorni} {giorni === 1 ? 'giorno' : 'giorni'}
      {urgenza === 'high' && <span className="hidden sm:inline">• SCADE!</span>}
    </span>
  )
}

// Card Entry nel Cestino
const TrashEntryCard = ({ 
  entry, 
  selected, 
  onSelect, 
  onView, 
  onRestore,
  onDelete,
  isCoordinatore 
}) => {
  const isPresent = entry.presente === true || entry.presente === 'Sì' || entry.presente === 'Presente'
  const urgenza = entry.urgenzaScadenza || 'low'
  const isHighUrgency = urgenza === 'high'
  
  return (
    <div className={`
      bg-white dark:bg-gray-800 rounded-lg border-2 transition-all
      ${isHighUrgency ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'}
      ${selected ? 'ring-2 ring-primary-500' : ''}
    `}>
      {/* Header con checkbox e info principali */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Checkbox */}
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => onSelect(entry._id, e.target.checked)}
            className="mt-1 h-4 w-4 text-primary-600 rounded focus:ring-primary-500"
          />
          
          {/* Info principale */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                👤 {entry.minore?.nomeCompleto || entry.minoreNome || 'N/D'}
              </h3>
              <ExpirationBadge giorni={entry.giorniRimanentiCestino} urgenza={urgenza} />
            </div>
            
            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <p>📅 {formatDate(entry.data)}</p>
              <p>👨‍🏫 {entry.educatore}</p>
              <p className="flex items-center gap-1">
                {isPresent ? (
                  <><CheckCircle className="h-3 w-3 text-green-500" /> Presente</>
                ) : (
                  <><XCircle className="h-3 w-3 text-orange-500" /> Assente</>
                )}
                {entry.orarioIngresso && ` • ${entry.orarioIngresso}`}
                {entry.orarioUscita && ` - ${entry.orarioUscita}`}
              </p>
            </div>
          </div>
        </div>
        
        {/* Motivo eliminazione */}
        <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">🗑️ Motivo eliminazione:</p>
          <p className="text-sm text-gray-700 dark:text-gray-300 italic">"{entry.deleteReason || 'Non specificato'}"</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            🕐 Eliminato il {formatDateTime(entry.deletedAt)} da {entry.deletedBy?.nome} {entry.deletedBy?.cognome}
          </p>
        </div>
      </div>
      
      {/* Azioni */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
        <button
          onClick={() => onView(entry)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <Eye className="h-4 w-4" />
          Dettagli
        </button>
        <button
          onClick={() => onRestore(entry)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
        >
          <RotateCcw className="h-4 w-4" />
          Ripristina
        </button>
        {isCoordinatore && (
          <button
            onClick={() => onDelete(entry)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          >
            <Trash className="h-4 w-4" />
            Elimina
          </button>
        )}
      </div>
    </div>
  )
}

// Modal Dettagli Entry
const EntryDetailModal = ({ entry, onClose, onRestore, onDelete, isCoordinatore }) => {
  if (!entry) return null
  
  const isPresent = entry.presente === true || entry.presente === 'Sì' || entry.presente === 'Presente'
  const urgenza = entry.urgenzaScadenza || 'low'
  
  // Campi da mostrare
  const sections = [
    {
      title: '🚪 Ingresso/Uscita',
      fields: [
        { label: 'Orario Ingresso', value: entry.orarioIngresso },
        { label: 'Accompagnato da', value: entry.accompagnatoIngresso },
        { label: 'Orario Uscita', value: entry.orarioUscita },
        { label: 'Ritirato da', value: entry.ritiratoDa }
      ]
    },
    {
      title: '😊 Comportamento',
      fields: [
        { label: 'Comportamenti', value: entry.comportamentiProblematici?.join(', ') || entry.comportamentiAggressivi?.join(', ') },
        { label: 'Descrizione', value: entry.descrizioneComportamenti }
      ]
    },
    {
      title: '📚 Compiti/Studio',
      fields: [
        { label: 'Svolgimento', value: entry.svolgimentoCompiti?.join(', ') },
        { label: 'Difficoltà', value: entry.difficoltaEsercizi }
      ]
    },
    {
      title: '🎯 Attività',
      fields: [
        { label: 'Attività gruppo', value: entry.attivitaGruppo?.join(', ') },
        { label: 'Tempo libero', value: entry.tempoLibero?.join(', ') },
        { label: 'Sport', value: entry.sport }
      ]
    },
    {
      title: '📝 Note',
      fields: [
        { label: 'Descrizione giornata', value: entry.descrizioneGiornata },
        { label: 'Interventi educativi', value: entry.interventiEducativi },
        { label: 'Situazione familiare', value: entry.situazioneFamiliare }
      ]
    }
  ]
  
  // Filtra sezioni con almeno un campo valorizzato
  const visibleSections = sections.filter(s => 
    s.fields.some(f => f.value && f.value.length > 0)
  )
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            📋 Registrazione Eliminata
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Content scrollabile */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Info principali */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              👤 {entry.minore?.nomeCompleto || entry.minoreNome}
            </p>
            <p className="text-gray-600 dark:text-gray-400">
              📅 {new Date(entry.data).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            <p className="text-gray-600 dark:text-gray-400">👨‍🏫 Educatore: {entry.educatore}</p>
          </div>
          
          {/* Presenza */}
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Presenza</p>
            <p className="flex items-center gap-2">
              {isPresent ? (
                <><CheckCircle className="h-5 w-5 text-green-500" /> <span className="text-green-700 dark:text-green-400">Presente</span></>
              ) : (
                <><XCircle className="h-5 w-5 text-orange-500" /> <span className="text-orange-700 dark:text-orange-400">Assente</span></>
              )}
            </p>
          </div>
          
          {/* Sezioni dati */}
          {visibleSections.map((section, idx) => (
            <div key={idx}>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 border-b border-gray-200 dark:border-gray-700 pb-1">
                {section.title}
              </h3>
              <div className="space-y-2">
                {section.fields.filter(f => f.value).map((field, fidx) => (
                  <div key={fidx}>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{field.label}</p>
                    <p className="text-sm text-gray-900 dark:text-white">{field.value}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          {/* Segnalazione urgente */}
          {entry.segnalazioneUrgente && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm font-semibold text-red-700 dark:text-red-400 flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" /> Segnalazione Urgente
              </p>
              <p className="text-sm text-red-600 dark:text-red-300 mt-1">{entry.dettaglioUrgenza}</p>
            </div>
          )}
          
          {/* Info eliminazione */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mt-4">
            <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-2">
              🗑️ Info Eliminazione
            </h3>
            <div className="space-y-1 text-sm">
              <p><span className="text-amber-600 dark:text-amber-500">Motivo:</span> <span className="text-gray-900 dark:text-white">"{entry.deleteReason}"</span></p>
              <p><span className="text-amber-600 dark:text-amber-500">Eliminato il:</span> <span className="text-gray-900 dark:text-white">{formatDateTime(entry.deletedAt)}</span></p>
              <p><span className="text-amber-600 dark:text-amber-500">Da:</span> <span className="text-gray-900 dark:text-white">{entry.deletedBy?.nome} {entry.deletedBy?.cognome}</span></p>
              <p className="flex items-center gap-2 mt-2">
                <span className="text-amber-600 dark:text-amber-500">Eliminazione definitiva tra:</span>
                <ExpirationBadge giorni={entry.giorniRimanentiCestino} urgenza={urgenza} />
              </p>
            </div>
          </div>
        </div>
        
        {/* Footer azioni */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          {isCoordinatore && (
            <button
              onClick={() => onDelete(entry)}
              className="px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            >
              🗑️ Elimina Subito
            </button>
          )}
          <button
            onClick={() => onRestore(entry)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            🔄 Ripristina
          </button>
        </div>
      </div>
    </div>
  )
}

// Modal Conferma Ripristino con opzione Merge
const RestoreModal = ({ 
  entry, 
  duplicate, 
  onClose, 
  onRestoreNew, 
  onMerge, 
  loading 
}) => {
  const [mode, setMode] = useState('new') // 'new' | 'merge'
  
  if (!entry) return null
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            🔄 Ripristina Registrazione
          </h2>
        </div>
        
        <div className="p-4 space-y-4">
          {duplicate ? (
            <>
              {/* Warning duplicato */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-3">
                <p className="text-sm font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" /> Attenzione: Esiste già una registrazione
                </p>
                <p className="text-sm text-amber-600 dark:text-amber-300 mt-1">
                  Per <strong>{entry.minore?.nomeCompleto}</strong> in data <strong>{formatDate(entry.data)}</strong> è già presente una registrazione creata da <strong>{duplicate.createdBy?.nome} {duplicate.createdBy?.cognome}</strong>.
                </p>
              </div>
              
              {/* Opzioni */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Cosa vuoi fare?</p>
                
                <label className={`block p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                  mode === 'new' 
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="restoreMode"
                    value="new"
                    checked={mode === 'new'}
                    onChange={() => setMode('new')}
                    className="sr-only"
                  />
                  <p className="font-medium text-gray-900 dark:text-white">🔄 Ripristina come nuova</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Avrai 2 registrazioni separate per lo stesso giorno</p>
                </label>
                
                <label className={`block p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                  mode === 'merge' 
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="restoreMode"
                    value="merge"
                    checked={mode === 'merge'}
                    onChange={() => setMode('merge')}
                    className="sr-only"
                  />
                  <p className="font-medium text-gray-900 dark:text-white">🔀 Unisci con esistente</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Combina i dati in un'unica registrazione (potrai scegliere quali dati mantenere)</p>
                </label>
              </div>
            </>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">
              Vuoi ripristinare la registrazione di <strong>{entry.minore?.nomeCompleto}</strong> del <strong>{formatDate(entry.data)}</strong>?
            </p>
          )}
        </div>
        
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            disabled={loading}
          >
            Annulla
          </button>
          <button
            onClick={() => mode === 'merge' ? onMerge(entry, duplicate) : onRestoreNew(entry, !!duplicate)}
            disabled={loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {loading ? 'Attendere...' : (mode === 'merge' ? 'Continua →' : 'Ripristina')}
          </button>
        </div>
      </div>
    </div>
  )
}

// Modal Merge
const MergeModal = ({ 
  sourceEntry, 
  targetEntry,
  customSections = [],
  fieldOptions = {},
  onClose, 
  onConfirm, 
  loading 
}) => {
  const [selections, setSelections] = useState({})
  
  // Calcola mergeFields e sections UNA SOLA VOLTA con lazy initialization
  const [fieldsData] = useState(() => {
    if (!sourceEntry || !targetEntry) {
      return { mergeFields: [], sections: {} }
    }
    
    // Helper functions
    const findSectionById = (sectionId) => {
      return customSections.find(s => 
        s.id === sectionId || 
        s.id === `section_${sectionId}` ||
        s.id.replace('section_', '') === sectionId
      )
    }
    
    const findOptionById = (section, optionId) => {
      if (!section?.options) return null
      return section.options.find(o => 
        o.id === optionId || 
        o.id === `opt_${optionId}` ||
        o.id.replace('opt_', '') === optionId ||
        optionId?.replace('opt_', '') === o.id?.replace('opt_', '')
      )
    }
    
    const findNoteById = (section, noteId) => {
      if (!section?.notes) return null
      return section.notes.find(n => 
        n.id === noteId || 
        n.id === `note_${noteId}` ||
        n.id.replace('note_', '') === noteId ||
        noteId?.replace('note_', '') === n.id?.replace('note_', '')
      )
    }
    
    const parseCustomFieldKey = (key) => {
      if (!key.startsWith('custom_section_')) return null
      
      const rest = key.replace('custom_section_', '')
      let sectionId, fieldType, extraId
      
      if (rest.includes('_options')) {
        const idx = rest.indexOf('_options')
        sectionId = rest.substring(0, idx)
        fieldType = 'options'
      } else if (rest.includes('_linkedNote_')) {
        const idx = rest.indexOf('_linkedNote_')
        sectionId = rest.substring(0, idx)
        fieldType = 'linkedNote'
        extraId = rest.substring(idx + '_linkedNote_'.length)
      } else if (rest.includes('_note_')) {
        const idx = rest.indexOf('_note_')
        sectionId = rest.substring(0, idx)
        fieldType = 'note'
        extraId = rest.substring(idx + '_note_'.length)
      } else {
        return null
      }
      
      const section = findSectionById(sectionId)
      const sectionName = section?.name || `Sezione ${sectionId}`
      
      if (fieldType === 'options') {
        return { sectionId, sectionName, type: 'options', label: `${sectionName} - Opzioni` }
      }
      
      if (fieldType === 'linkedNote') {
        const option = findOptionById(section, extraId)
        const optionLabel = option?.label || extraId
        const noteLabel = option?.linkedNote || 'Note'
        return { sectionId, sectionName, type: 'linkedNote', optionId: extraId, label: `${sectionName} - ${noteLabel} (${optionLabel})` }
      }
      
      if (fieldType === 'note') {
        const note = findNoteById(section, extraId)
        const noteLabel = note?.label || extraId
        return { sectionId, sectionName, type: 'note', noteId: extraId, label: `${sectionName} - ${noteLabel}` }
      }
      
      return null
    }
    
    const optionIdsToLabels = (optionIds, sectionId) => {
      if (!Array.isArray(optionIds)) return String(optionIds)
      const section = findSectionById(sectionId)
      if (!section) return optionIds.join(', ')
      
      return optionIds.map(id => {
        const option = findOptionById(section, id)
        return option?.label || id
      }).join(', ')
    }
    
    // Helper per tradurre value -> label per campi standard (comportamenti, compiti, etc.)
    const standardValueToLabel = (fieldKey, value) => {
      const options = fieldOptions[fieldKey] || []
      if (!options || options.length === 0) return value
      
      const option = options.find(o => 
        (typeof o === 'string' && o.toLowerCase().replace(/\s+/g, '_') === value) ||
        (typeof o === 'string' && o === value) ||
        (typeof o === 'object' && (o.value === value || o.label === value))
      )
      
      return option 
        ? (typeof option === 'string' ? option : option.label) 
        : value
    }
    
    const standardArrayToLabels = (fieldKey, values) => {
      if (!Array.isArray(values) || values.length === 0) return '(vuoto)'
      return values.map(v => standardValueToLabel(fieldKey, v)).join(', ')
    }
    
    // Campi standard
    const standardMergeFields = [
      { key: 'orarioIngresso', label: 'Orario Ingresso', section: '🚪 Ingresso' },
      { key: 'accompagnatoIngresso', label: 'Accompagnato da', section: '🚪 Ingresso' },
      { key: 'orarioUscita', label: 'Orario Uscita', section: '🚶 Uscita' },
      { key: 'ritiratoDa', label: 'Ritirato da', section: '🚶 Uscita' },
      { key: 'comportamentiProblematici', label: 'Comportamenti', section: '😊 Comportamento', isArray: true },
      { key: 'descrizioneComportamenti', label: 'Descrizione comportamenti', section: '😊 Comportamento' },
      { key: 'svolgimentoCompiti', label: 'Svolgimento compiti', section: '📚 Compiti', isArray: true },
      { key: 'difficoltaEsercizi', label: 'Difficoltà esercizi', section: '📚 Compiti' },
      { key: 'attivitaGruppo', label: 'Attività gruppo', section: '🎯 Attività', isArray: true },
      { key: 'tempoLibero', label: 'Tempo libero', section: '🎯 Attività', isArray: true },
      { key: 'descrizioneGiornata', label: 'Descrizione giornata', section: '📝 Note' },
      { key: 'interventiEducativi', label: 'Interventi educativi', section: '📝 Note' },
      { key: 'situazioneFamiliare', label: 'Situazione familiare', section: '📝 Note' },
      { key: 'annotazioniGenitore', label: 'Annotazioni genitore', section: '📝 Note' },
      { key: 'segnalazioneUrgente', label: 'Segnalazione urgente', section: '⚠️ Urgenze', isBoolean: true },
      { key: 'dettaglioUrgenza', label: 'Dettaglio urgenza', section: '⚠️ Urgenze' }
    ]
    
    // Estrai customFields
    const sourceCustomFields = sourceEntry.customFields || {}
    const targetCustomFields = targetEntry.customFields || {}
    
    const allCustomFieldKeys = new Set([
      ...Object.keys(sourceCustomFields),
      ...Object.keys(targetCustomFields)
    ])
    
    const customMergeFields = Array.from(allCustomFieldKeys).map(key => {
      const parsed = parseCustomFieldKey(key)
      
      return {
        key: `customFields.${key}`,
        rawKey: key,
        label: parsed?.label || key,
        section: `⚙️ ${parsed?.sectionName || 'Sezioni Personalizzate'}`,
        isCustomField: true,
        customFieldType: parsed?.type,
        sectionId: parsed?.sectionId
      }
    })
    
    const allFields = [...standardMergeFields, ...customMergeFields]
    
    // Raggruppa per sezione
    const groupedSections = {}
    allFields.forEach(field => {
      if (!groupedSections[field.section]) groupedSections[field.section] = []
      groupedSections[field.section].push(field)
    })
    
    // Pre-calcola displayValues per evitare ricalcoli nel render
    const displayValues = {}
    const hasValues = {}
    
    allFields.forEach(field => {
      // Source value
      let sourceVal, targetVal
      
      if (field.isCustomField) {
        const srcRaw = sourceCustomFields[field.rawKey]
        const tgtRaw = targetCustomFields[field.rawKey]
        
        if (field.customFieldType === 'options') {
          let srcArr = srcRaw
          let tgtArr = tgtRaw
          
          if (typeof srcRaw === 'string') {
            try { srcArr = JSON.parse(srcRaw) } catch { srcArr = srcRaw ? [srcRaw] : [] }
          }
          if (typeof tgtRaw === 'string') {
            try { tgtArr = JSON.parse(tgtRaw) } catch { tgtArr = tgtRaw ? [tgtRaw] : [] }
          }
          
          sourceVal = Array.isArray(srcArr) && srcArr.length > 0 ? optionIdsToLabels(srcArr, field.sectionId) : '(vuoto)'
          targetVal = Array.isArray(tgtArr) && tgtArr.length > 0 ? optionIdsToLabels(tgtArr, field.sectionId) : '(vuoto)'
          hasValues[`${field.key}_source`] = Array.isArray(srcArr) && srcArr.length > 0
          hasValues[`${field.key}_target`] = Array.isArray(tgtArr) && tgtArr.length > 0
        } else {
          sourceVal = srcRaw || '(vuoto)'
          targetVal = tgtRaw || '(vuoto)'
          hasValues[`${field.key}_source`] = !!srcRaw
          hasValues[`${field.key}_target`] = !!tgtRaw
        }
      } else {
        const srcRaw = sourceEntry[field.key]
        const tgtRaw = targetEntry[field.key]
        
        if (field.isArray) {
          sourceVal = standardArrayToLabels(field.key, srcRaw)
          targetVal = standardArrayToLabels(field.key, tgtRaw)
          hasValues[`${field.key}_source`] = Array.isArray(srcRaw) && srcRaw.length > 0
          hasValues[`${field.key}_target`] = Array.isArray(tgtRaw) && tgtRaw.length > 0
        } else if (field.isBoolean) {
          sourceVal = srcRaw ? 'Sì' : 'No'
          targetVal = tgtRaw ? 'Sì' : 'No'
          hasValues[`${field.key}_source`] = true
          hasValues[`${field.key}_target`] = true
        } else {
          sourceVal = srcRaw || '(vuoto)'
          targetVal = tgtRaw || '(vuoto)'
          hasValues[`${field.key}_source`] = !!srcRaw
          hasValues[`${field.key}_target`] = !!tgtRaw
        }
      }
      
      displayValues[`${field.key}_source`] = sourceVal
      displayValues[`${field.key}_target`] = targetVal
    })
    
    return { 
      mergeFields: allFields, 
      sections: groupedSections,
      displayValues,
      hasValues
    }
  })
  
  const { mergeFields, sections, displayValues, hasValues } = fieldsData
  
  // Handler per selezione
  const handleSelection = (fieldKey, value) => {
    setSelections(prev => ({ ...prev, [fieldKey]: value }))
  }
  
  // Seleziona tutto
  const selectAll = (source) => {
    const newSelections = {}
    mergeFields.forEach(field => {
      newSelections[field.key] = source
    })
    setSelections(newSelections)
  }
  
  if (!sourceEntry || !targetEntry) return null
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            🔀 Unisci Registrazioni
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            👤 {sourceEntry.minore?.nomeCompleto} • 📅 {formatDate(sourceEntry.data)}
          </p>
        </div>
        
        {/* Info colonne */}
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 flex">
          <div className="flex-1" />
          <div className="w-1/3 text-center text-sm font-medium text-gray-600 dark:text-gray-400">
            🗑️ Dal Cestino<br/>
            <span className="text-xs">({sourceEntry.educatore})</span>
          </div>
          <div className="w-1/3 text-center text-sm font-medium text-gray-600 dark:text-gray-400">
            📄 Esistente<br/>
            <span className="text-xs">({targetEntry.educatore})</span>
          </div>
        </div>
        
        {/* Content scrollabile */}
        <div className="p-4 space-y-4 overflow-y-auto" style={{ maxHeight: '50vh', overscrollBehavior: 'contain' }}>
          {Object.entries(sections).map(([sectionName, fields]) => (
            <div key={sectionName}>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 border-b border-gray-200 dark:border-gray-700 pb-1">
                {sectionName}
              </h3>
              
              <div className="space-y-3">
                {fields.map(field => {
                  const sourceVal = displayValues[`${field.key}_source`]
                  const targetVal = displayValues[`${field.key}_target`]
                  const sourceHas = hasValues[`${field.key}_source`]
                  const targetHas = hasValues[`${field.key}_target`]
                  const bothEmpty = !sourceHas && !targetHas
                  const onlyOneHas = (sourceHas && !targetHas) || (!sourceHas && targetHas)
                  const different = sourceVal !== targetVal && !bothEmpty
                  
                  // Auto-select se solo uno ha valore
                  const currentSelection = selections[field.key] || 
                    (onlyOneHas ? (sourceHas ? 'source' : 'target') : null)
                  
                  return (
                    <div key={field.key} className={`flex items-start gap-2 p-2 rounded-lg ${different && !onlyOneHas ? 'bg-amber-50 dark:bg-amber-900/10' : ''}`}>
                      <div className="flex-1 text-sm text-gray-700 dark:text-gray-300">
                        {field.label}
                        {onlyOneHas && <span className="text-xs text-gray-400 ml-1">(auto)</span>}
                      </div>
                      
                      {/* Opzione Source */}
                      <div 
                        onClick={() => !bothEmpty && handleSelection(field.key, 'source')}
                        className={`w-1/3 p-2 rounded cursor-pointer text-xs break-words ${
                          currentSelection === 'source' 
                            ? 'bg-primary-100 dark:bg-primary-900/30 border-2 border-primary-500' 
                            : 'bg-gray-100 dark:bg-gray-700 border-2 border-transparent hover:border-gray-300'
                        } ${!sourceHas ? 'opacity-50' : ''} ${bothEmpty ? 'cursor-not-allowed' : ''}`}
                      >
                        <span>{sourceVal}</span>
                      </div>
                      
                      {/* Opzione Target */}
                      <div 
                        onClick={() => !bothEmpty && handleSelection(field.key, 'target')}
                        className={`w-1/3 p-2 rounded cursor-pointer text-xs break-words ${
                          currentSelection === 'target' 
                            ? 'bg-primary-100 dark:bg-primary-900/30 border-2 border-primary-500' 
                            : 'bg-gray-100 dark:bg-gray-700 border-2 border-transparent hover:border-gray-300'
                        } ${!targetHas ? 'opacity-50' : ''} ${bothEmpty ? 'cursor-not-allowed' : ''}`}
                      >
                        <span>{targetVal}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
        
        {/* Quick actions */}
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 flex justify-center gap-4">
          <button
            onClick={() => selectAll('source')}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Seleziona tutti dal Cestino
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={() => selectAll('target')}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Seleziona tutti da Esistente
          </button>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            disabled={loading}
          >
            ← Indietro
          </button>
          <button
            onClick={() => onConfirm(selections)}
            disabled={loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {loading ? 'Unione in corso...' : '🔀 Unisci Registrazioni'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Modal Svuota Cestino
// FIX: onConfirm ora riceve confirmText come parametro
const EmptyTrashModal = ({ stats, onClose, onConfirm, loading }) => {
  const [confirmText, setConfirmText] = useState('')
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-red-600">
            🧹 Svuota Cestino
          </h2>
        </div>
        
        <div className="p-4 space-y-4">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-sm font-semibold text-red-700 dark:text-red-400 flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" /> Questa azione è IRREVERSIBILE!
            </p>
          </div>
          
          <p className="text-gray-600 dark:text-gray-400">
            Stai per eliminare definitivamente:
          </p>
          
          <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
            <li>• <strong>{stats?.totale || 0}</strong> registrazioni</li>
          </ul>
          
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Per confermare, scrivi <strong>SVUOTA</strong> nel campo sottostante:
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Scrivi SVUOTA"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
        
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            disabled={loading}
          >
            Annulla
          </button>
          {/* FIX: Passa confirmText a onConfirm */}
          <button
            onClick={() => onConfirm(confirmText)}
            disabled={loading || confirmText !== 'SVUOTA'}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Eliminazione...' : '🗑️ Elimina Tutto'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Componente principale
export default function TrashPage() {
  const { user, tenant } = useAuth()
  const { showSuccess, showError } = useNotification()
  
  const isCoordinatore = user?.role === 'coordinatore' || user?.role === 'superadmin'
  
  // Stati
  const [entries, setEntries] = useState([])
  const [stats, setStats] = useState(null)
  const [activity, setActivity] = useState([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  
  // Filtri
  const [filters, setFilters] = useState({
    search: '',
    minore: '',
    educatore: '',
    urgenza: ''
  })
  const [showFilters, setShowFilters] = useState(false)
  
  // Selezione multipla
  const [selectedIds, setSelectedIds] = useState([])
  
  // Modali
  const [detailEntry, setDetailEntry] = useState(null)
  const [restoreEntry, setRestoreEntry] = useState(null)
  const [restoreDuplicate, setRestoreDuplicate] = useState(null)
  const [mergeSource, setMergeSource] = useState(null)
  const [mergeTarget, setMergeTarget] = useState(null)
  const [showEmptyModal, setShowEmptyModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null)
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)
  
  // Activity log espanso
  const [showActivity, setShowActivity] = useState(false)
  
  // Carica dati
  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      
      const [entriesRes, statsRes, auditLogRes] = await Promise.all([
        trashApi.list(filters),
        trashApi.stats(),
        trashApi.auditLog(30)
      ])
      
      setEntries(entriesRes.data?.data?.entries || [])
      setStats(statsRes.data?.data || null)
      setActivity(auditLogRes.data?.data?.logs || [])
      
    } catch (error) {
      console.error('Load trash error:', error)
      showError('Errore nel caricamento del cestino')
    } finally {
      setLoading(false)
    }
  }, [filters])
  
  useEffect(() => {
    loadData()
  }, [loadData])
  
  // Gestione selezione
  const handleSelect = (id, selected) => {
    setSelectedIds(prev => 
      selected ? [...prev, id] : prev.filter(i => i !== id)
    )
  }
  
  const handleSelectAll = (selected) => {
    setSelectedIds(selected ? entries.map(e => e._id) : [])
  }
  
  // Ripristino singolo
  const handleRestore = async (entry) => {
    try {
      setActionLoading(true)
      
      // Check duplicato
      const checkRes = await trashApi.checkRestore(entry._id)
      const { hasDuplicate, existingEntry } = checkRes.data?.data || {}
      
      if (hasDuplicate) {
        setRestoreEntry(entry)
        setRestoreDuplicate(existingEntry)
      } else {
        // Ripristina direttamente
        await trashApi.restore(entry._id)
        showSuccess('Registrazione ripristinata')
        loadData()
      }
      
    } catch (error) {
      showError(error.response?.data?.message || 'Errore nel ripristino')
    } finally {
      setActionLoading(false)
    }
  }
  
  // Ripristino forzato (con duplicato)
  const handleRestoreForced = async (entry, hasDuplicate) => {
    try {
      setActionLoading(true)
      await trashApi.restore(entry._id, true)
      showSuccess('Registrazione ripristinata')
      setRestoreEntry(null)
      setRestoreDuplicate(null)
      loadData()
    } catch (error) {
      showError(error.response?.data?.message || 'Errore nel ripristino')
    } finally {
      setActionLoading(false)
    }
  }
  
  // Avvia merge
  const handleStartMerge = (source, target) => {
    setRestoreEntry(null)
    setRestoreDuplicate(null)
    setMergeSource(source)
    setMergeTarget(target)
  }
  
  // Conferma merge
  const handleConfirmMerge = async (selections) => {
    try {
      setActionLoading(true)
      await trashApi.merge(mergeSource._id, mergeTarget._id, selections)
      showSuccess('Registrazioni unite con successo')
      setMergeSource(null)
      setMergeTarget(null)
      loadData()
    } catch (error) {
      showError(error.response?.data?.message || 'Errore nell\'unione')
    } finally {
      setActionLoading(false)
    }
  }
  
  // Ripristino multiplo
  const handleBulkRestore = async () => {
    if (selectedIds.length === 0) return
    
    try {
      setActionLoading(true)
      const res = await trashApi.bulkRestore(selectedIds, true)
      const { restored, duplicates, errors } = res.data?.data || {}
      
      let message = `Ripristinate ${restored?.length || 0} registrazioni`
      if (duplicates?.length > 0) {
        message += `, ${duplicates.length} saltate (duplicati)`
      }
      
      showSuccess(message)
      setSelectedIds([])
      loadData()
    } catch (error) {
      showError(error.response?.data?.message || 'Errore nel ripristino multiplo')
    } finally {
      setActionLoading(false)
    }
  }
  
  // Eliminazione definitiva singola
  const handlePermanentDelete = async (entry) => {
    try {
      setActionLoading(true)
      await trashApi.deletePermanent(entry._id)
      showSuccess('Registrazione eliminata definitivamente')
      setShowDeleteConfirm(null)
      setDetailEntry(null)
      loadData()
    } catch (error) {
      showError(error.response?.data?.message || 'Errore nell\'eliminazione')
    } finally {
      setActionLoading(false)
    }
  }
  
  // Eliminazione multipla - mostra conferma
  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return
    setShowBulkDeleteConfirm(true)
  }
  
  // Conferma eliminazione multipla
  const confirmBulkDelete = async () => {
    try {
      setActionLoading(true)
      await trashApi.bulkDelete(selectedIds)
      showSuccess(`Eliminate definitivamente ${selectedIds.length} registrazioni`)
      setSelectedIds([])
      setShowBulkDeleteConfirm(false)
      loadData()
    } catch (error) {
      showError(error.response?.data?.message || 'Errore nell\'eliminazione multipla')
    } finally {
      setActionLoading(false)
    }
  }
  
  // Svuota cestino
  // FIX: Ora riceve confirmText come parametro dal modal
  const handleEmptyTrash = async (confirmText) => {
    try {
      setActionLoading(true)
      await trashApi.empty(confirmText)
      showSuccess('Cestino svuotato')
      setShowEmptyModal(false)
      loadData()
    } catch (error) {
      showError(error.response?.data?.message || 'Errore nello svuotamento')
    } finally {
      setActionLoading(false)
    }
  }
  
  // Filtro entries
  const filteredEntries = useMemo(() => {
    let result = entries
    
    if (filters.search) {
      const search = filters.search.toLowerCase()
      result = result.filter(e => 
        e.minore?.nomeCompleto?.toLowerCase().includes(search) ||
        e.educatore?.toLowerCase().includes(search) ||
        e.deleteReason?.toLowerCase().includes(search)
      )
    }
    
    if (filters.urgenza) {
      result = result.filter(e => e.urgenzaScadenza === filters.urgenza)
    }
    
    return result
  }, [entries, filters.search, filters.urgenza])
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    )
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Trash2 className="h-7 w-7" />
            Cestino
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
            Le registrazioni vengono eliminate definitivamente dopo {stats?.retentionDays || 15} giorni
          </p>
        </div>
        
        {isCoordinatore && entries.length > 0 && (
          <button
            onClick={() => setShowEmptyModal(true)}
            className="px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-2"
          >
            🧹 Svuota Cestino
          </button>
        )}
      </div>
      
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totale || 0}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Totale nel cestino</p>
          </Card>
          <Card className="p-4">
            <p className="text-2xl font-bold text-red-600">{stats.inScadenza || 0}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">In scadenza (3 giorni)</p>
          </Card>
          <Card className="p-4">
            <p className="text-2xl font-bold text-green-600">{stats.ripristinatiOggi || 0}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Ripristinati oggi</p>
          </Card>
          <Card className="p-4">
            <p className="text-2xl font-bold text-amber-600">{stats.eliminatiOggi || 0}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Eliminati oggi</p>
          </Card>
        </div>
      )}
      
      {/* Filtri e ricerca */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Ricerca */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca per nome, educatore, motivo..."
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          {/* Filtro urgenza */}
          <select
            value={filters.urgenza}
            onChange={(e) => setFilters(f => ({ ...f, urgenza: e.target.value }))}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="">Tutte le urgenze</option>
            <option value="high">🔴 In scadenza</option>
            <option value="medium">🟡 Entro 7 giorni</option>
            <option value="low">🟢 Più di 7 giorni</option>
          </select>
        </div>
        
        {/* Selezione multipla */}
        {filteredEntries.length > 0 && (
          <div className="mt-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedIds.length === filteredEntries.length && filteredEntries.length > 0}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="h-4 w-4 text-primary-600 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Seleziona tutto ({filteredEntries.length})
              </span>
            </label>
            
            {selectedIds.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  {selectedIds.length} selezionati
                </span>
                <button
                  onClick={handleBulkRestore}
                  disabled={actionLoading}
                  className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  🔄 Ripristina
                </button>
                {isCoordinatore && (
                  <button
                    onClick={handleBulkDelete}
                    disabled={actionLoading}
                    className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                  >
                    🗑️ Elimina
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </Card>
      
      {/* Lista entries */}
      {filteredEntries.length === 0 ? (
        <EmptyState
          icon={Trash2}
          title="Cestino vuoto"
          description="Non ci sono registrazioni nel cestino"
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {filteredEntries.map(entry => (
            <TrashEntryCard
              key={entry._id}
              entry={entry}
              selected={selectedIds.includes(entry._id)}
              onSelect={handleSelect}
              onView={setDetailEntry}
              onRestore={handleRestore}
              onDelete={(e) => setShowDeleteConfirm(e)}
              isCoordinatore={isCoordinatore}
            />
          ))}
        </div>
      )}
      
      {/* Log Attività */}
      <Card className="overflow-hidden">
        <button
          onClick={() => setShowActivity(!showActivity)}
          className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          <span className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Attività Recente {activity.length > 0 && `(${activity.length})`}
          </span>
          {showActivity ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </button>
        
        {showActivity && (
          <div className="border-t border-gray-200 dark:border-gray-700">
            {activity.length === 0 ? (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Nessuna attività registrata</p>
                <p className="text-xs mt-1">Le azioni sul cestino verranno mostrate qui</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-96 overflow-y-auto">
                {activity.map((item, idx) => {
                  // Icona e colore per tipo azione
                  const actionConfig = {
                    trash: { icon: '🗑️', label: 'spostato nel cestino', color: 'text-yellow-600' },
                    restore: { icon: '🔄', label: 'ripristinato', color: 'text-green-600' },
                    permanent_delete: { icon: '❌', label: 'eliminato definitivamente', color: 'text-red-600' },
                    bulk_delete: { icon: '🗑️', label: 'eliminazione multipla', color: 'text-red-600' },
                    empty_trash: { icon: '🧹', label: 'cestino svuotato', color: 'text-red-600' },
                    merge: { icon: '🔀', label: 'unito', color: 'text-blue-600' }
                  }
                  const config = actionConfig[item.action] || { icon: '📝', label: item.action, color: 'text-gray-600' }
                  
                  return (
                    <div key={idx} className="px-4 py-3 flex items-start gap-3">
                      <span className="text-lg">{config.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 dark:text-white">
                          {item.minoreNome ? (
                            <>
                              <strong>{item.minoreNome}</strong> {config.label}
                            </>
                          ) : item.action === 'empty_trash' ? (
                            <>
                              <span className={config.color}>Cestino svuotato</span>
                              {item.details?.deletedCount && ` (${item.details.deletedCount} registrazioni)`}
                            </>
                          ) : item.action === 'bulk_delete' ? (
                            <>
                              <span className={config.color}>Eliminazione multipla</span>
                              {item.details?.deletedCount && ` (${item.details.deletedCount} registrazioni)`}
                            </>
                          ) : (
                            <span className={config.color}>{config.label}</span>
                          )}
                          <span className="text-gray-500"> da {item.userName}</span>
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatRelativeTime(item.createdAt)}
                          {item.reason && ` • "${item.reason}"`}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </Card>
      
      {/* Modali */}
      {detailEntry && (
        <EntryDetailModal
          entry={detailEntry}
          onClose={() => setDetailEntry(null)}
          onRestore={handleRestore}
          onDelete={(e) => setShowDeleteConfirm(e)}
          isCoordinatore={isCoordinatore}
        />
      )}
      
      {restoreEntry && (
        <RestoreModal
          entry={restoreEntry}
          duplicate={restoreDuplicate}
          onClose={() => { setRestoreEntry(null); setRestoreDuplicate(null); }}
          onRestoreNew={handleRestoreForced}
          onMerge={handleStartMerge}
          loading={actionLoading}
        />
      )}
      
      {mergeSource && mergeTarget && (
        <MergeModal
          sourceEntry={mergeSource}
          targetEntry={mergeTarget}
          customSections={tenant?.settings?.customSections || []}
          fieldOptions={tenant?.settings?.fieldOptions || {}}
          onClose={() => { setMergeSource(null); setMergeTarget(null); }}
          onConfirm={handleConfirmMerge}
          loading={actionLoading}
        />
      )}
      
      {showEmptyModal && (
        <EmptyTrashModal
          stats={stats}
          onClose={() => setShowEmptyModal(false)}
          onConfirm={handleEmptyTrash}
          loading={actionLoading}
        />
      )}
      
      {showDeleteConfirm && (
        <ConfirmModal
          isOpen={true}
          onClose={() => setShowDeleteConfirm(null)}
          onConfirm={() => handlePermanentDelete(showDeleteConfirm)}
          title="Elimina Definitivamente"
          message={`Vuoi eliminare definitivamente la registrazione di ${showDeleteConfirm.minore?.nomeCompleto} del ${formatDate(showDeleteConfirm.data)}? Questa azione è irreversibile.`}
          confirmText="Elimina"
          danger
        />
      )}
      
      {showBulkDeleteConfirm && (
        <ConfirmModal
          isOpen={true}
          onClose={() => setShowBulkDeleteConfirm(false)}
          onConfirm={confirmBulkDelete}
          title="Elimina Definitivamente"
          message={`Vuoi eliminare definitivamente ${selectedIds.length} registrazioni selezionate? Questa azione è irreversibile.`}
          confirmText={`Elimina ${selectedIds.length}`}
          danger
          loading={actionLoading}
        />
      )}
    </div>
  )
}
